import type { SimulationProgress, SimulationResult } from '@openrocket-web/physics';
import { configLabel } from '../state/configs';
import { configOf, isStale, simLabel, type AppState, type Simulation } from '../state/app';
import type { Analysis } from '../state/derived';
import type { Fmt } from './format';
import { Icon, type IconName } from './icons';
import { STATUS_TEXT } from './Results';
import { BlockedNotice } from './SimulationPanel';

export interface RunningState {
  readonly id: string;
  readonly progress: SimulationProgress | undefined;
  /** Cuántas quedan por lanzar después de la que corre, cuando se lanzan varias seguidas. */
  readonly waiting: number;
}

export type SelectMode = 'one' | 'toggle' | 'range';

interface Props {
  readonly state: Pick<AppState, 'rocket' | 'motors' | 'sims' | 'configs' | 'activeConfigId'>;
  /** Simulaciones seleccionadas; la primera es la principal (la que se edita y se enseña en la vista). */
  readonly selectedIds: readonly string[];
  readonly analysis: Analysis;
  readonly fmt: Fmt;
  readonly running: RunningState | undefined;
  readonly failure: string | undefined;
  readonly onSelect: (id: string, mode: SelectMode) => void;
  readonly onNew: () => void;
  readonly onEdit: (id: string) => void;
  readonly onDelete: (ids: readonly string[]) => void;
  readonly onRun: (ids: readonly string[]) => void;
  readonly onResults: (id: string) => void;
  readonly onCancel: () => void;
}

const PHASE_LABEL = { pad: 'En la rampa', rod: 'En la varilla', flight: 'En vuelo', recovery: 'Descenso con paracaídas', tumble: 'Descenso en volteo', landed: 'Aterrizado' } as const;

type Status = 'none' | 'ok' | 'stale' | 'bad' | 'running';

const STATUS_ICON: Record<Status, IconName> = { none: 'status-none', ok: 'status-ok', stale: 'status-stale', bad: 'status-bad', running: 'tool-run' };

function statusOf(state: Props['state'], sim: Simulation, running: RunningState | undefined): { status: Status; text: string } {
  if (running?.id === sim.id) return { status: 'running', text: 'Simulando…' };
  if (sim.run === undefined) return { status: 'none', text: 'Sin simular' };
  if (isStale(state, sim)) return { status: 'stale', text: 'Desactualizada' };
  const r = sim.run.result.status;
  return r === 'completed' ? { status: 'ok', text: 'Al día' } : { status: 'bad', text: STATUS_TEXT[r] };
}

const NA = 'N/A';

/** Valores de las columnas de resultados, en el orden de `FLIGHT_HEADERS`; sin resultados son todos «N/A». */
export function flightColumns(result: SimulationResult | undefined, fmt: Fmt): string[] {
  if (result === undefined) return Array.from({ length: 9 }, () => NA);
  const s = result.summary;
  const finite = (v: number | undefined): v is number => v !== undefined && Number.isFinite(v);
  const at = (type: string) => result.events.find((e) => e.type === type);
  const deploy = at('recovery-device-deployment');
  const burnout = at('burnout');
  const apogee = at('apogee');
  return [
    finite(s.rodClearanceVelocity) ? fmt.speedText(s.rodClearanceVelocity) : NA,
    finite(s.apogee) ? fmt.distance(s.apogee) : NA,
    finite(deploy?.velocity) ? fmt.speedText(deploy.velocity) : NA,
    burnout !== undefined && apogee !== undefined ? fmt.seconds(apogee.time - burnout.time) : NA,
    finite(s.maxVelocity) ? fmt.speedText(s.maxVelocity) : NA,
    finite(s.maxAcceleration) ? fmt.accelText(s.maxAcceleration) : NA,
    finite(s.timeToApogee) ? fmt.seconds(s.timeToApogee) : NA,
    finite(s.flightTime) ? fmt.seconds(s.flightTime) : NA,
    finite(s.landingVelocity) ? fmt.speedText(s.landingVelocity) : NA,
  ];
}

const FLIGHT_HEADERS = ['Velocidad al abandonar la varilla', 'Apogeo', 'Velocidad al abrir el paracaídas', 'Retardo óptimo', 'Velocidad máxima', 'Aceleración máxima', 'Tiempo hasta el apogeo', 'Duración del vuelo', 'Velocidad de llegada al suelo'];

/** Pestaña «Simulaciones de vuelo»: la lista de simulaciones, con varias selecciones y los resultados en columnas. */
export function SimulationsTab({ state, selectedIds, analysis, fmt, running, failure, onSelect, onNew, onEdit, onDelete, onRun, onResults, onCancel }: Props): JSX.Element {
  const primary = state.sims.find((s) => s.id === selectedIds[0]);
  const chosen = state.sims.filter((s) => selectedIds.includes(s.id));
  const busy = running !== undefined;
  const runningSim = running === undefined ? undefined : state.sims.find((s) => s.id === running.id);

  return (
    <section aria-labelledby="sims-title" className="tabpage">
      <h2 id="sims-title" className="sr-only">
        Simulaciones de vuelo
      </h2>
      <div className="bigbar" role="toolbar" aria-label="Acciones sobre las simulaciones">
        <button type="button" onClick={onNew}>
          <Icon name="tool-new" />
          Nueva simulación
        </button>
        <button type="button" disabled={primary === undefined} onClick={() => primary !== undefined && onEdit(primary.id)}>
          <Icon name="tool-edit" />
          Editar la simulación
        </button>
        <button type="button" disabled={chosen.length === 0 || !analysis.valid || busy} onClick={() => onRun(chosen.map((s) => s.id))}>
          <Icon name="tool-run" />
          Lanzar las simulaciones
        </button>
        <button type="button" disabled={chosen.length === 0} onClick={() => onDelete(chosen.map((s) => s.id))}>
          <Icon name="tool-delete" />
          Borrar las simulaciones
        </button>
        <button type="button" disabled={primary?.run === undefined} onClick={() => primary !== undefined && onResults(primary.id)}>
          <Icon name="tool-plot" />
          Exportar / Gráfica
        </button>
        <button type="button" disabled={!busy} onClick={onCancel}>
          Cancelar
        </button>
      </div>

      <BlockedNotice analysis={analysis} />
      {failure !== undefined && (
        <p className="notice notice--error" role="alert">
          {failure}
        </p>
      )}

      <div className="progress" role="status" aria-live="polite">
        {running !== undefined && runningSim !== undefined && (
          <>
            <progress aria-label="Progreso de la simulación" />
            <span>
              {simLabel(runningSim)}:{' '}
              {running.progress === undefined ? 'preparando…' : `${PHASE_LABEL[running.progress.phase]} · t = ${running.progress.time.toFixed(1)} s · altitud ${fmt.distance(running.progress.altitude, 3)}`}
              {running.waiting > 0 ? ` (${running.waiting} más en espera)` : ''}. Hay una simulación en curso: espera a que termine o cancélala.
            </span>
          </>
        )}
      </div>

      <div className="region region--table">
        {state.sims.length === 0 ? (
          <p className="muted pad">No hay simulaciones. Crea una con «Nueva simulación».</p>
        ) : (
          <table className="grid grid--sims" aria-label="Simulaciones">
            <thead>
              <tr>
                <th scope="col">
                  <span className="sr-only">Estado</span>
                </th>
                <th scope="col">Nombre</th>
                <th scope="col">Configuración</th>
                {FLIGHT_HEADERS.map((h) => (
                  <th scope="col" key={h}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.sims.map((sim) => {
                const on = selectedIds.includes(sim.id);
                const st = statusOf(state, sim, running);
                const cols = flightColumns(sim.run?.result, fmt);
                return (
                  <tr
                    key={sim.id}
                    className={on ? 'is-active' : undefined}
                    onClick={(e) => onSelect(sim.id, e.shiftKey ? 'range' : e.ctrlKey || e.metaKey ? 'toggle' : 'one')}
                    onDoubleClick={() => onEdit(sim.id)}
                  >
                    <td className="grid__status" title={st.text}>
                      <Icon name={STATUS_ICON[st.status]} />
                      <span className="sr-only">{st.text}</span>
                    </td>
                    <th scope="row">
                      <button type="button" className="grid__select" aria-current={on ? 'true' : undefined}>
                        {simLabel(sim)}
                      </button>
                    </th>
                    <td>{configLabel(configOf(state, sim), state.rocket, state.motors)}</td>
                    {cols.map((c, i) => (
                      <td key={i}>{c}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
